import { useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CalendarDays, ArrowLeft, User, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { useSeoHead } from "@/lib/head";
import { useSite } from "@/context/SiteContext";
import { Breadcrumbs, ContentSections, FaqSection } from "@/components/public/PageParts";
import LeadForm from "@/components/public/LeadForm";
import PreviewBanner from "@/components/public/PreviewBanner";
import NotFound from "./NotFound";

function fmtDate(d, lang) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(lang === "tr" ? "tr-TR" : lang, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
}

export default function BlogPost() {
  const { lang, postSlug } = useParams();
  const { setSwitcher } = useSite();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const fullSlug = `blog/${postSlug}`;

  const { data: page, isError, isLoading } = useQuery({
    queryKey: ["blog-post", lang, postSlug, preview ? "prev" : "pub"],
    queryFn: async () => preview
      ? (await api.get(`/preview/page/${lang}/${fullSlug}`)).data
      : (await api.get(`/public/page/${lang}/${fullSlug}`)).data,
    retry: false,
  });

  useSeoHead(page);
  useEffect(() => { if (page?.switcher) setSwitcher(page.switcher); return () => setSwitcher(null); }, [page, setSwitcher]);

  if (isLoading) return <div className="container-x py-32 text-subtle">…</div>;
  if (isError || !page) return <NotFound />;

  const c = page.content;
  const blog = page.blog || {};
  const blogPath = preview ? `/${lang}/blog?preview=1` : `/${lang}/blog`;
  const catPath = blog.category ? `/${lang}/blog?cat=${blog.category}` : blogPath;

  return (
    <article>
      {page.preview && <PreviewBanner lang={lang} status={page.preview_meta?.status} updatedAt={page.preview_meta?.updated_at} />}
      <header className="relative min-h-[52vh] flex items-end">
        {page.image && <img src={page.image} alt={page.seo?.image_alt || ""} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/40" />
        <div className="relative container-x pb-12 pt-28">
          <Breadcrumbs crumbs={c.breadcrumbs} dir={page.dir} />
          <div className="flex flex-wrap items-center gap-4 mt-5 text-xs uppercase tracking-widest">
            {blog.category_label && <Link to={catPath} data-testid="blog-category-badge" className="px-2.5 py-1 rounded-full bg-copper/15 text-copper hover:bg-copper/25 transition-colors">{blog.category_label}</Link>}
            {blog.date && <span className="flex items-center gap-1.5 text-copper"><CalendarDays size={13} /> {fmtDate(blog.date, lang)}</span>}
          </div>
          <h1 data-testid="blog-post-h1" className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink mt-3 max-w-4xl">{c.h1}</h1>
          {blog.author && <p className="flex items-center gap-2 text-subtle text-sm mt-4" data-testid="blog-author"><User size={14} className="text-copper" /> {t(lang, "writtenBy")} {blog.author}</p>}
        </div>
      </header>

      <div className="container-x py-12 md:py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <p className="text-lg text-subtle leading-relaxed max-w-3xl">{c.intro}</p>
          <ContentSections sections={c.sections} />
          <FaqSection faq={c.faq} title={t(lang, "faqTitle")} />

          {blog.tags?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-12" data-testid="blog-tags">
              <Tag size={15} className="text-copper" />
              {blog.tags.map((tag, i) => (
                <span key={i} data-testid={`blog-tag-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-subtle">{tag}</span>
              ))}
            </div>
          )}

          {page.related?.links?.length > 0 && (
            <section className="mt-14" data-testid="blog-related">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-ink mb-6">{page.related.heading}</h2>
              <div className="flex flex-wrap gap-3">
                {page.related.links.map((l, i) => (
                  <Link key={i} to={l.path.replace(/\/$/, "") || l.path} data-testid={`blog-related-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-sm text-ink hover:border-copper hover:text-copper transition-colors">
                    {l.anchor} <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </section>
          )}

          <Link to={blogPath.replace(/\/$/, "")} data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-subtle hover:text-copper mt-12">
            <ArrowLeft size={16} /> {t(lang, "backToBlog")}
          </Link>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit" id="quote">
          <p className="eyebrow mb-3">{t(lang, "quoteTitle")}</p>
          <LeadForm />
        </aside>
      </div>
    </article>
  );
}
